import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import type { OpeningScheduleResponse } from 'generated/index';
import { formatDate } from 'utils/date';

import { Alert, Typography } from '@mui/material';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import type { TableCRUDLayoutHandlers } from 'components/shared/CRUD/TableCRUDLayout';

import type { OpeningHoursProps } from './OpeningHours.types';
import {
  StyledOpeningHours,
  StyledOverviewHeader,
  StyledOverviewSection
} from './OpeningHours.styles';
import CopyOpeningScheduleDialog from './components/CopyOpeningScheduleDialog/CopyOpeningScheduleDialog';
import EffectiveOpeningOverview from './components/EffectiveOpeningOverview/EffectiveOpeningOverview';
import OpeningScheduleForm from './components/OpeningScheduleForm/OpeningScheduleForm';
import { useDeleteOpeningSchedule } from './hooks/useDeleteOpeningSchedule';
import { useGetEffectiveOpeningHours } from './hooks/useGetEffectiveOpeningHours';
import { useGetOpeningSchedules } from './hooks/useGetOpeningSchedules';
import { useOpeningScheduleColumns } from './hooks/useOpeningScheduleColumns';
import { getWeeksOnDate } from './utils/effectiveOpeningHours';
import {
  extendSchedule,
  findScheduleCoveringDate
} from './utils/openingHoursDates';

/**
 * The tab "Openingstijden" on the plan unit card: the plan unit's own opening schedules, and for the
 * chosen date what applies per day type and weekday, inherited answers included.
 */
const OpeningHours = ({ planUnitId, date, canEdit }: OpeningHoursProps) => {
  'use memo';
  const { t } = useTranslation('common', {
    keyPrefix: 'planUnitHome.openingHours'
  });
  // isLoading, not isFetching: after a save the list refetches, and the grid, the notice and the
  // overview would otherwise disappear and come back.
  const { data: schedules = [], isLoading } =
    useGetOpeningSchedules(planUnitId);
  const columns = useOpeningScheduleColumns();
  const { mutate: deleteSchedule } = useDeleteOpeningSchedule(planUnitId);
  const { data: effective = [], isLoading: isLoadingEffective } =
    useGetEffectiveOpeningHours(planUnitId, date);
  const [template, setTemplate] = useState<OpeningScheduleResponse>();
  const [copySource, setCopySource] = useState<OpeningScheduleResponse>();
  const [copyId, setCopyId] = useState<string>();
  const [gridHandlers, setGridHandlers] =
    useState<TableCRUDLayoutHandlers<OpeningScheduleResponse>>();

  const scheduleOnDate = findScheduleCoveringDate(schedules, date);
  const weeksOnDate = getWeeksOnDate(effective, date);

  const onExtendClick = (
    _id: number | string,
    row?: OpeningScheduleResponse,
    handlers?: TableCRUDLayoutHandlers<OpeningScheduleResponse>
  ) => {
    if (!row) return;
    setTemplate(extendSchedule(row, schedules));
    handlers?.handleOpen();
  };

  const onCopyClick = (
    _id: number | string,
    row?: OpeningScheduleResponse,
    handlers?: TableCRUDLayoutHandlers<OpeningScheduleResponse>
  ) => {
    if (!row) return;
    setGridHandlers(handlers);
    setCopySource(row);
  };

  // The copy is already saved and answers only its id: open it in the drawer, so the administrator
  // can adjust its lines, as soon as the refetched list holds it.
  const onCopied = (savedCopyId: string) => {
    setCopySource(undefined);
    setTemplate(undefined);
    setCopyId(savedCopyId);
  };
  const copy = copyId
    ? schedules.find((schedule) => schedule.openingScheduleId === copyId)
    : undefined;
  useEffect(() => {
    if (!copy) return;
    setCopyId(undefined);
    gridHandlers?.handleEditClick?.(copy.openingScheduleId, copy);
  }, [copy, gridHandlers]);

  return (
    <StyledOpeningHours>
      <TableCRUDLayout<OpeningScheduleResponse>
        FormComponent={(props) => (
          // An extension wins over rowData: the layout only clears rowData once the drawer has
          // finished closing, so right after an edit it can still hold the previous row.
          <OpeningScheduleForm
            planUnitId={planUnitId}
            rowData={template ? undefined : props.rowData}
            template={template}
            handleClose={props.handleClose}
          />
        )}
        rows={schedules}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.openingScheduleId}
        shouldRenderToolBar={false}
        dynamicHeight
        customHeight="auto"
        pagination={false}
        editable={canEdit}
        hideCreateAction={!canEdit}
        disableActions={!canEdit}
        onAddClick={() => setTemplate(undefined)}
        onClose={() => setTemplate(undefined)}
        {...(canEdit && {
          onDeleteClick: (ids: string[]) => deleteSchedule(ids[0]),
          customActions: (_id, row) => [
            {
              tooltipTitle: t('copy'),
              icon: 'copy',
              onClick: onCopyClick
            },
            ...(row && extendSchedule(row, schedules)
              ? [
                  {
                    tooltipTitle: t('extend'),
                    icon: 'arrow-right' as const,
                    onClick: onExtendClick
                  }
                ]
              : [])
          ]
        })}
        confirmationTitle={t('deleteTitle')}
        confirmationBody={t('deleteBody')}
        customCreateTitle={t('createTitle')}
        customEditTitle={t('editTitle')}
        drawerWidth={960}
      />

      <StyledOverviewSection>
        <StyledOverviewHeader>
          <Typography variant="h6" component="h2">
            {t('overviewTitle', { date: formatDate(date?.toISOString()) })}
          </Typography>
        </StyledOverviewHeader>
        {!isLoading && !scheduleOnDate && (
          <Alert severity="info">{t('noScheduleOnDate')}</Alert>
        )}
        {!isLoadingEffective && (
          <EffectiveOpeningOverview weeks={weeksOnDate} />
        )}
      </StyledOverviewSection>

      <CopyOpeningScheduleDialog
        planUnitId={planUnitId}
        schedule={copySource}
        onClose={() => setCopySource(undefined)}
        onCopied={onCopied}
      />
    </StyledOpeningHours>
  );
};

export default OpeningHours;
